<template>

  <div class="inner-page-section" v-if="affiliate">

    <div v-if="affiliate.isDeactivated">
      <div class="alert alert-danger">
        <h4 style="margin-bottom: 0;">Affiliate is deactivated</h4>
      </div>
    </div>

    <div class="two-column-grid">

      <!-- Left: Affiliate Details -->
      <div class="card">
        <div class="card-header">
          <i class="fa fa-user card-header--icon"></i>
          <span class="card-header--title">Affiliate Details</span>
        </div>
        <div class="card-content">
          <div class="section">

            <div class="form-group">
              <label>Auth uid: {{ affiliate.authUid }}</label>
            </div>

            <div class="form-group">
              <label>Username <input type="text" placeholder="Username" v-model="affiliate.name" /></label>
            </div>

            <div class="form-group">
              <label>Password <input type="text" placeholder="Password" v-model="affiliate.password" /></label>
            </div>

            <div class="form-group">
              <label>Contact email <input type="email" placeholder="Email" v-model="affiliate.contactEmail" /></label>
            </div>

            <div class="form-group">
              <label>Affiliation codes <input type="text"
                  placeholder="Add the list of codes separated by space or comma"
                  v-model="affiliate.masterCode" /></label>
              <div>
                <AffiliationBubble v-for="code in affiliationCodes" :affiliate="affiliate" :code="code" :key="code" />
              </div>
            </div>

            <div class="form-group">
              <label>Affiliate color</label>
              <input name="color" type="color" v-model="affiliate.color" id="inputColor" required />
            </div>

            <div class="form-group">
                <label class="checkbox">
                    <input class="checkbox-input" type="checkbox" v-model="affiliate.hasAccessToBookings" />
                    <span class="checkbox-label">Enable access to bookings</span>
                </label>
            </div>

          </div>

          <div class="section">
            <span class="section-title">Categorization</span>

            <div class="form-group">
              <label>Partner type
                <select v-model="affiliate.partnerType">
                  <option v-for="partnerType in partnerTypes" :value="partnerType" :key="partnerType">
                    {{ partnerType }}
                  </option>
                </select>
              </label>
            </div>

            <div class="form-group">
              <label>Partner category
                <select v-model="affiliate.partnerCategory">
                  <option v-for="partnerCategory in partnerCategoriesOptions" :value="partnerCategory"
                    :key="partnerCategory">
                    {{ partnerCategory }}
                  </option>
                </select>
              </label>
            </div>
          </div>


          <div class="section">
            <span class="section-title">Allowed user requests</span>
            <div class="form-group">
                <label class="checkbox">
                    <input class="checkbox-input" type="checkbox" v-model="affiliate.cancellationsEnabled" />
                    <span class="checkbox-label">Enable cancellation requests</span>
                </label>
            </div>

            <div class="form-group">
                <label class="checkbox">
                    <input class="checkbox-input" type="checkbox" v-model="affiliate.modificationsEnabled" />
                    <span class="checkbox-label">Enable modification requests</span>
                </label>
            </div>

            <div class="form-group">
                <label class="checkbox">
                    <input class="checkbox-input" type="checkbox" v-model="affiliate.openTicketsEnabled" />
                    <span class="checkbox-label">Enable open-ticket requests</span>
                </label>
            </div>

          </div>

          <div class="section">
            <span class="section-title">Email Settings</span>

            <p>
              <i class="fa fa-exclamation-triangle text-warning"></i> Email settings will only have an effect on API
              or Whitelabel bookings.
              Bookings that are performed through Ferryhopper website or application will follow the global email
              rules of Ferryhopper. If you want more information on what these rules mean, <a
                href="https://app.clickup.com/t/9015210430/FH-3922" target="_blank">visit this link</a>.
            </p>

            <div class="form-group">
              <label>Partner Email</label>
              <input type="text" v-model="affiliate.emailSettings.partnerEmail" placeholder="Partner email" />
            </div>

            <div class="form-group">
              <div v-for="(val, key) in affiliate.emailSettings.receiverSettings" :key="'receiver-' + key"
                class="input-group">
                <span class="input-group__label">{{ communicationMethodName(key) }}</span>
                <select v-model="affiliate.emailSettings.receiverSettings[key]" class="input-group__input">
                  <option v-for="option in receiverOptions(key)" :value="option" :key="option">{{ option }}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>



      <div class="card">
        <div class="card-header">
          <i class="fa fa-lock card-header--icon"></i>
          <span class="card-header--title">Model Details</span>
        </div>
        <div class="card-content">
          <div class="section">

            <div class="form-group">
              <label>Affiliation Model
                <select v-model="affiliate.affiliationModel">
                  <option v-for="model in affiliationModels" :value="model.value" :key="model.value">{{ model.title }}
                  </option>
                </select>
              </label>
            </div>

            <div class="form-group">
              <label>Commission (without fee) <input type="text" placeholder="0"
                  v-model.number="affiliate.fixedCommission" /></label>
            </div>
            <div class="form-group">
              <label>Commission (with fee) <input type="text" placeholder="0"
                  v-model.number="affiliate.feeCommission" /></label>
            </div>
            <div class="form-group">
              <label>Booking Fee <input type="number" placeholder="0" v-model.number="affiliate.serviceFee" /></label>
            </div>
            <div class="form-group">
              <label>Booking Fee Mode
                <select v-model="affiliate.serviceFeeMode">
                  <option v-for="serviceFeeMode in serviceFeeModes" :value="serviceFeeMode" :key="serviceFeeMode">{{
                    serviceFeeMode }}</option>
                </select>
              </label>
            </div>

            <div class="form-group" v-if="isEditing">
              <label>Changes will have take effect from:</label>
              <select v-model="modelChangeMonth">
                <option value="CURRENT">Start of current month</option>
                <option value="NEXT">Start of next month</option>
              </select>
            </div>

            <button type="button" class="save-btn" @click="onSaveClicked">
              <span v-if="isEditing">Update settings & scheme</span>
              <span v-else>Create affiliate</span>
              <span v-if="isWaiting"><i class="fa fa-spinner fa-spin"></i></span>
            </button>
          </div>
        </div>
      </div>
    </div>


    <div class="card" v-if="isEditing">
      <div class="card-header">
        <i class="fa fa-file card-header--icon"></i>
        <span class="card-header--title">Pending Invoices</span>
      </div>
      <table class="card-table">
        <thead>
          <tr>
            <th>Month</th>
            <th>Year</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in invoicesList" :key="invoice.id">
            <td>{{ invoice.month }}</td>
            <td>{{ invoice.year }}</td>
            <td>{{ invoice.amount }}</td>
            <td>
              <span :class="['pill', invoice.paid ? 'paid' : 'pending']">
                {{ invoice.paid ? 'Paid' : 'Pending' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="pending-total">
        Total Pending: <strong>{{ totalInvoiceAmount }}</strong>
      </div>
    </div>


    <div class="two-column-grid">
      <!-- Widget API Key Card -->
      <div class="card" v-if="isEditing">
        <div class="card-header">
          <i class="fa fa-key card-header--icon"></i>
          <span class="card-header--title">Widget API Key</span>
        </div>
        <div class="card-content">
          <div class="section" v-if="affiliate.widgetApiKeyValue">
            <p>This affiliate has a widget API key for the embeddable widget:</p>
            <div class="code-block">
              <pre><code>{{ affiliate.widgetApiKeyValue }}</code></pre>
              <button class="copy-btn copy-btn--code" @click="copyToClipboard(affiliate.widgetApiKeyValue)">
                <i class="fa fa-clipboard"></i>
              </button>
            </div>
            <div class="delete-confirmation" v-if="showDeleteApiKeyConfirm">
              <p class="warning-text"><i class="fa fa-exclamation-triangle"></i> Are you sure? The embeddable widget will stop working.</p>
              <div class="button-group">
                <button class="danger-btn" @click="confirmDeleteApiKey" :disabled="isApiKeyLoading">
                  <span v-if="isApiKeyLoading"><i class="fa fa-spinner fa-spin"></i></span>
                  <span v-else>Yes, Delete</span>
                </button>
                <button class="cancel-btn" @click="showDeleteApiKeyConfirm = false" :disabled="isApiKeyLoading">
                  Cancel
                </button>
              </div>
            </div>
            <button v-else class="danger-btn mt10" @click="showDeleteApiKeyConfirm = true" :disabled="isApiKeyLoading">
              Delete API Key
            </button>
          </div>
          <div class="section" v-else>
            <p>No widget API key exists for this affiliate. Create one to enable the embeddable widget.</p>
            <button class="save-btn" @click="onCreateApiKey" :disabled="isApiKeyLoading">
              <span v-if="isApiKeyLoading"><i class="fa fa-spinner fa-spin"></i></span>
              <span v-else>Create API Key</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Whitelabel Configuration Navigation Card -->
      <div class="card" v-if="isEditing">
        <div class="card-header">
          <i class="fa fa-cog card-header--icon"></i>
          <span class="card-header--title">Whitelabel Configuration</span>
        </div>
        <div class="card-content">
          <div class="form-group">
            <p>Manage whitelabel settings for this affiliate.</p>
            <button class="save-btn" @click="goToWhitelabelConfig">
              Go to Whitelabel Configuration
            </button>
          </div>
        </div>
      </div>

      <div class="danger-zone-card" v-if="!affiliate.isDeactivated && isEditing">
        <div class="danger-header">
          <i class="fa fa-exclamation-triangle danger-icon"></i>
          <span class="card-title danger-title">Danger Zone</span>
        </div>
        <div class="danger-desc">
          Deactivation means that the affiliate will no longer be able to access his personal dashboard.
          However, no data loss will occur. Deactivating a user cannot be undone.
        </div>
        <div class="flex-row flex-row--end">
          <button class="danger-btn mt10" v-if="!isWaiting" @click="onDeactivationSubmit()">Deactivate user</button>
        </div>
      </div>
    </div>
  </div>


</template>

<script>
import { schema } from '@/models/schema'
import AffiliationBubble from '@/components/shared/AffiliationBubble'
import ApiGateway from '../services/ApiGateway'
import { createAffiliate } from '../models/createAffiliate';
import { partnerCategoriesEnum, receiverTargetEnum, reportsReceiverTargetEnum } from '../models/schema';


export default {
  name: 'UserRegistrationComponent',
  components: {
    AffiliationBubble
  },
  props: {
    code: {
      type: String,
      required: false,
    },
  },
  data: () => ({
    partnerTypes: ['B2C', 'B2B'],
    partnerCategoriesOptions: partnerCategoriesEnum,
    receiverTargetOptions: receiverTargetEnum,
    reportReceiverTargetOptions: reportsReceiverTargetEnum,
    affiliate: undefined,
    isWaiting: true,
    isEditing: false,
    isApiKeyLoading: false,
    showDeleteApiKeyConfirm: false,
    invoicesList: [],
    modelChangeMonth: 'CURRENT',
    serviceFeeModes: [
      'ABSOLUTE',
      'RELATIVE'
    ],
    affiliationModels: window.ferryhopper && window.ferryhopper.affiliation_models
  }),
  created() {
    this.initializeComponent(this.code);
  },
  watch: {
    code(newCode) {
      this.initializeComponent(newCode);
    }
  },
  methods: {
    receiverOptions(method) {
      if (method === 'cancellationReport' || method === 'bulkActionReport') {
        return this.reportReceiverTargetOptions;
      }
      return this.receiverTargetOptions;
    },
    communicationMethodName(method) {
      switch (method) {
        case 'bookingRefund':
          return 'Booking Refund';
        case 'bulkActionReport':
          return 'Bulk Action Report';
        case 'bookingError':
          return 'Booking Error';
        case 'bookingConfirmation':
          return 'Booking Confirmation';
        case 'tripCancellation':
          return 'Trip Cancellation';
        case 'cancellationReport':
          return 'Cancellation Report';
        case 'bulkModification':
          return 'Bulk Modification';
        case 'modificationRequest':
          return 'Modification Request';
        case 'bookingCurrentState':
          return 'Modification Confirmation';
        case 'bookingPartialRefund':
          return 'Partial Refund';
        default:
          return method;
      }
    },
    initializeComponent(code) {
      if (!code) {
        this.affiliate = createAffiliate();
        this.isWaiting = false;
        this.isEditing = false;
        this.invoicesList = [];
      } else {
        this.isEditing = true;
        this.isWaiting = true;

        ApiGateway.getAffiliateByCode(code).then(response => {
          this.affiliate = createAffiliate(response.data.user);
          this.invoicesList = response.data.user.invoices;
          this.isWaiting = false;
        })
      }
    },
    async onDeactivationSubmit() {
      try {
        this.isWaiting = true;
        await ApiGateway.deactivateAffiliate(this.affiliate);
        this.affiliate.isDeactivated = true;
      } catch (error) {
        this.errorNotify('An error has occurred');
      }
      this.isWaiting = false;
    },
    async onSaveClicked() {
      try {
        this.isWaiting = true;
        await schema.validate(this.affiliate);

        if (this.isEditing) {
          await ApiGateway.editAffiliateScheme({
            ...this.affiliate,
            isInstantAffect: this.modelChangeMonth === 'CURRENT'
          });
        } else {
          await ApiGateway.register(this.affiliate);
        }
        this.onRegistrationResponse();
      } catch (error) {
        this.onRegistrationError(error)
      }
      this.isWaiting = false;
    },
    goToWhitelabelConfig() {
      this.$router.push({ name: 'CreateWhitelabel', params: { code: this.code } });
    },
    onRegistrationResponse() {
      this.onRegistrationSuccess();
    },
    onRegistrationSuccess() {
      this.$store.dispatch('notificationsModule/successNotify', `Affiliate ${this.affiliate.name} has been ${this.isEditing ? 'edited' : 'created'}`)
      if (!this.isEditing) {
        this.affiliate = createAffiliate();
      }
    },
    onRegistrationError(error) {
      this.$store.dispatch('notificationsModule/errorNotify', error.toString().replace('ValidationError: ', 'Input error: '));
    },
    async onCreateApiKey() {
      try {
        this.isApiKeyLoading = true;
        const result = await ApiGateway.createWidgetApiKey(this.affiliate.code);
        this.affiliate.widgetApiKeyId = result.keyId;
        this.affiliate.widgetApiKeyValue = result.keyValue;
        this.$store.dispatch('notificationsModule/successNotify', 'Widget API key created successfully');
      } catch (error) {
        this.$store.dispatch('notificationsModule/errorNotify', error.message || 'Failed to create API key');
      }
      this.isApiKeyLoading = false;
    },
    async confirmDeleteApiKey() {
      try {
        this.isApiKeyLoading = true;
        await ApiGateway.deleteWidgetApiKey(this.affiliate.code);
        this.affiliate.widgetApiKeyId = null;
        this.affiliate.widgetApiKeyValue = null;
        this.showDeleteApiKeyConfirm = false;
        this.$store.dispatch('notificationsModule/successNotify', 'Widget API key deleted successfully');
      } catch (error) {
        this.$store.dispatch('notificationsModule/errorNotify', error.message || 'Failed to delete API key');
      }
      this.isApiKeyLoading = false;
    },
    copyToClipboard(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
        this.$store.dispatch('notificationsModule/successNotify', 'Copied to clipboard');
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('Copy');
        document.body.removeChild(textarea);
        this.$store.dispatch('notificationsModule/successNotify', 'Copied to clipboard');
      }
    },
  },
  computed: {
    affiliationCodes() {
      if (!this.affiliate.masterCode) {
        return [];
      }

      return (this.affiliate.masterCode || '').replace(/ /g, ',').replace(/,,/g, ',').split(',');
    },
    totalInvoiceAmount() {
      if (!this.invoicesList || this.invoicesList.length === 0) {
        return 0;
      }

      return this.invoicesList
        .reduce((sum, inv) => sum + inv.amount, 0);
    },
  }
}
</script>


<style scoped>
.danger-zone-card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 2px 8px var(--gray-light);
  padding: 2rem 2.2rem 1.5rem 2.2rem;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  border-top: 3px solid #ff5e3a;
}

.danger-header {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 1.1rem;
}

.danger-icon {
  color: #ff5e3a;
  font-size: 1.5rem;
}

.danger-title {
  color: #ff5e3a;
  letter-spacing: 0.5px;
}


.danger-desc {
  color: #ff5e3a;
  font-size: 0.97rem;
  margin-top: 0.1rem;
  line-height: 1.5;
}


.paid {
  background: #d4f7dc;
  color: #2e7d32;
}

.pending {
  background: #ffe0e0;
  color: #c62828;
}

.pending-total {
  font-size: 1.1em;
  color: #c62828;
  font-weight: 600;
  margin-top: 1rem;
}
/* Custom alert-danger styling for deactivated affiliate */
.alert.alert-danger {
  background: #ffe0e0;
  border: 1px solid #c62828;
  color: #c62828;
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 8px rgba(198, 40, 40, 0.08);
}

.alert.alert-danger h4 {
  margin-bottom: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: #c62828;
}

.code-block {
  position: relative;
  background-color: #2d2d2d;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 1rem;
}

.code-block pre {
  margin: 0;
  padding: 1rem;
  overflow-x: auto;
}

.code-block code {
  background: none;
  color: #f8f8f2;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.85rem;
  line-height: 1.5;
  white-space: pre;
}

.copy-btn--code {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  padding: 0.4rem 0.6rem;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.copy-btn--code:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.mt10 {
  margin-top: 10px;
}

.delete-confirmation {
  background-color: #fff8f8;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  padding: 1rem;
  margin-top: 10px;
}

.warning-text {
  color: #c62828;
  margin: 0 0 0.75rem 0;
  font-size: 0.9rem;
}

.warning-text i {
  margin-right: 0.5rem;
}

.button-group {
  display: flex;
  gap: 0.5rem;
}

.cancel-btn {
  background-color: #9e9e9e;
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.cancel-btn:hover {
  background-color: #757575;
}

.cancel-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
